import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as api from '../../api';
import Spinner from '../Atoms/Spinner';
import GetProjectMainImage from '../Utils/GetProjectMainImage';
import { createUrlWithRedirect } from '../Utils/redirectUtils';
import { NeedToPay, IsExpiringSoon } from '../Utils/Validator';
import { PaymentPopupV2 } from '../Components/PaymentPopup';
import { getUserRegistrations } from '../Components/SDE24/sdeSupabaseService';

const MEDIA_BASE = process.env.REACT_APP_MEDIA_BASE_URL + "userfiles/";

// --- Styled Components ---

const DashboardContainer = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: 50px 30px 80px;

    @media (max-width: 768px) {
        padding: 30px 16px 60px;
    }
`;

const HeroSection = styled.div`
    display: flex;
    align-items: center;
    gap: 28px;
    margin-bottom: 36px;

    @media (max-width: 768px) {
        flex-direction: column;
        text-align: center;
        gap: 16px;
    }
`;

const AvatarCircle = styled.div`
    width: 96px;
    height: 96px;
    min-width: 96px;
    border-radius: 50%;
    background: #0039d6;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 3px solid #0039d6;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    span {
        color: white;
        font-size: 32px;
        font-weight: 600;
        letter-spacing: 1px;
    }
`;

const HeroInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;

    h1 {
        font-size: 32px;
        font-weight: 600;
        margin: 0;
        color: black;
    }

    .job-title {
        font-size: 16px;
        color: #444;
        font-weight: 400;
    }

    .company-name {
        font-size: 14px;
        color: #0039d6;
        font-weight: 500;
    }
`;

const CompletionSection = styled.div`
    margin-bottom: 32px;
    padding: 20px 24px;
    background: #f8f9fa;
    border-radius: 10px;
    border: 1px solid #e9ecef;
`;

const CompletionLabel = styled.div`
    font-size: 14px;
    color: #555;
    margin-bottom: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    strong {
        color: #0039d6;
        font-size: 15px;
    }
`;

const CompletionBarContainer = styled.div`
    width: 100%;
    height: 8px;
    background: #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
`;

const CompletionBarFill = styled.div`
    height: 100%;
    width: ${props => props.$percentage}%;
    background: #0039d6;
    border-radius: 4px;
    transition: width 0.6s ease;
`;

const QuickStatsRow = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 36px;

    @media (max-width: 768px) {
        flex-wrap: wrap;
        gap: 12px;
    }
`;

const StatItem = styled.div`
    flex: 1;
    text-align: center;
    padding: 18px 12px;
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 10px;

    @media (max-width: 768px) {
        flex: 0 0 calc(50% - 6px);
    }

    .stat-number {
        font-size: 28px;
        font-weight: 700;
        color: #0039d6;
        line-height: 1.2;
    }

    .stat-label {
        font-size: 12px;
        color: #777;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-top: 4px;
    }
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const DashboardCard = styled.div`
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
    transition: box-shadow 0.2s ease, transform 0.2s ease;
    display: flex;
    flex-direction: column;

    &:hover {
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.07);
        transform: translateY(-1px);
    }
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f0f0f0;

    h3 {
        font-size: 16px;
        font-weight: 600;
        margin: 0;
        color: #222;
    }
`;

const EditLink = styled(Link)`
    font-size: 13px;
    font-weight: 500;
    color: #0039d6;
    text-decoration: none;
    padding: 4px 12px;
    border: 1px solid #0039d6;
    border-radius: 6px;
    transition: all 0.15s ease;

    &:hover {
        background: #0039d6;
        color: white;
    }
`;

const CardBody = styled.div`
    flex: 1;
`;

const ProfileInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    .info-row {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: #444;
    }

    .info-icon {
        width: 16px;
        text-align: center;
        color: #999;
    }

    .social-row {
        display: flex;
        gap: 12px;
        margin-top: 8px;
    }

    .social-badge {
        font-size: 11px;
        padding: 3px 8px;
        border-radius: 4px;
        background: #f0f4ff;
        color: #0039d6;
        font-weight: 500;
    }

    .social-badge.inactive {
        background: #f5f5f5;
        color: #bbb;
    }
`;

const CompanyInfo = styled.div`
    display: flex;
    gap: 16px;
    align-items: flex-start;

    .company-logo {
        width: 56px;
        height: 56px;
        min-width: 56px;
        border-radius: 8px;
        object-fit: contain;
        background: #f8f9fa;
        border: 1px solid #e9ecef;
    }

    .company-details {
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 14px;
        color: #444;

        .company-title {
            font-weight: 600;
            color: #222;
            font-size: 15px;
        }

        a {
            color: #0039d6;
            text-decoration: none;
            font-size: 13px;
            &:hover { text-decoration: underline; }
        }
    }

    .membership-badges {
        display: flex;
        gap: 6px;
        margin-top: 6px;
        flex-wrap: wrap;
    }

    .badge {
        font-size: 11px;
        padding: 2px 8px;
        border-radius: 4px;
        background: #e8f5e9;
        color: #2e7d32;
        font-weight: 500;
    }
`;

const ProjectThumbnailGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const ProjectThumbnail = styled.div`
    aspect-ratio: 1;
    border-radius: 8px;
    overflow: hidden;
    background: #f5f5f5;
    border: 1px solid #e9ecef;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const POSList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const POSItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child { border-bottom: none; }

    .pos-logo {
        width: 40px;
        height: 40px;
        min-width: 40px;
        border-radius: 6px;
        object-fit: contain;
        background: #f8f9fa;
        border: 1px solid #e9ecef;
    }

    .pos-placeholder {
        width: 40px;
        height: 40px;
        min-width: 40px;
        border-radius: 6px;
        background: #f0f4ff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        color: #0039d6;
    }

    .pos-info {
        font-size: 14px;
        .pos-title { font-weight: 500; color: #222; }
        .pos-address { color: #777; font-size: 13px; margin-top: 2px; }
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 24px 16px;
    color: #999;
    font-size: 14px;

    a {
        display: inline-block;
        margin-top: 10px;
        color: #0039d6;
        font-weight: 500;
        text-decoration: none;
        &:hover { text-decoration: underline; }
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
`;

const RenewalBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 20px;
    margin-bottom: 24px;
    background: #fff8e1;
    border: 1px solid #ffe082;
    border-radius: 10px;

    @media (max-width: 768px) {
        flex-direction: column;
        text-align: center;
    }

    .renewal-text {
        font-size: 14px;
        color: #5d4037;
        line-height: 1.4;

        strong {
            display: block;
            font-size: 15px;
            color: #e65100;
            margin-bottom: 2px;
        }
    }

    .renewal-button {
        padding: 10px 20px;
        font-size: 14px;
        font-weight: 600;
        border-radius: 8px;
        cursor: pointer;
        background: #0039d6;
        color: white;
        border: none;
        white-space: nowrap;
        transition: background 0.15s ease;

        &:hover {
            background: #0029a6;
        }
    }
`;

const MembershipBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    margin-top: 10px;

    &.active {
        background: #e8f5e9;
        color: #2e7d32;
    }

    &.inactive {
        background: #fff3e0;
        color: #e65100;
    }
`;

const AccountSection = styled.div`
    margin-top: 40px;
    padding: 24px;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 12px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        margin: 0 0 16px;
        color: #222;
    }
`;

const AccountActions = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

const ActionButton = styled.button`
    padding: 10px 18px;
    font-size: 13px;
    font-weight: 500;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.15s ease;
    border: 1px solid;

    &.reset-password {
        background: white;
        border-color: #0039d6;
        color: #0039d6;

        &:hover {
            background: #f0f4ff;
        }
    }

    &.delete-account {
        background: white;
        border-color: #dc3545;
        color: #dc3545;

        &:hover {
            background: #fff5f5;
        }
    }
`;

const ConfirmPopup = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
`;

const ConfirmPopupInner = styled.div`
    background: white;
    border-radius: 12px;
    padding: 32px;
    max-width: 480px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);

    h3 {
        font-size: 18px;
        font-weight: 600;
        margin: 0 0 12px;
        color: #222;
    }

    p {
        font-size: 14px;
        color: #555;
        line-height: 1.5;
        margin: 0 0 20px;
    }

    .popup-actions {
        display: flex;
        gap: 10px;
        justify-content: flex-end;
    }

    .popup-cancel {
        padding: 10px 18px;
        font-size: 13px;
        font-weight: 500;
        border-radius: 8px;
        cursor: pointer;
        background: #f5f5f5;
        border: 1px solid #ddd;
        color: #444;

        &:hover { background: #eee; }
    }

    .popup-confirm {
        padding: 10px 18px;
        font-size: 13px;
        font-weight: 500;
        border-radius: 8px;
        cursor: pointer;
        background: #dc3545;
        border: none;
        color: white;

        &:hover { background: #c82333; }
    }

    select {
        width: 100%;
        padding: 10px 14px;
        border: 1px solid #d0d0d0;
        border-radius: 6px;
        font-size: 14px;
        margin-bottom: 16px;
    }

    .warning {
        font-size: 13px;
        color: #dc3545;
        font-weight: 500;
        margin-bottom: 16px;
    }
`;

const FeedbackMessage = styled.div`
    margin-top: 12px;
    padding: 10px 14px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;

    &.success {
        background: #e8f5e9;
        color: #2e7d32;
    }

    &.error {
        background: #ffebee;
        color: #c62828;
    }
`;

// --- Helpers ---

function calculateCompleteness(user) {
    if (!user) return { percentage: 0, filled: 0, total: 0 };

    const fields = [
        !!user.hsProfile?.firstname,
        !!user.hsProfile?.lastname,
        !!user.hsProfile?.jobtitle,
        !!user.hsProfile?.email,
        !!(user.hsProfile?.phone || user.hsProfile?.mobilephone),
        !!user.hsProfile?.city,
        !!user.hsProfile?.country,
        !!user.user?.portrait,
        !!(user.hsProfile?.presentation_libre_fr || user.hsProfile?.presentation_libre_en),
        !!user.hsCompany?.properties?.name,
        !!user.company?.logo,
        !!user.hsCompany?.properties?.website,
        !!user.hsCompany?.properties?.address,
        !!(user.hsCompany?.properties?.description_courte_fr || user.hsCompany?.properties?.description_courte_en),
        user.company?.projects?.length > 0,
    ];

    const filled = fields.filter(Boolean).length;
    return { percentage: Math.round((filled / fields.length) * 100), filled, total: fields.length };
}

function getInitials(user) {
    const first = user?.hsProfile?.firstname || user?.user?.first_name || '';
    const last = user?.hsProfile?.lastname || user?.user?.last_name || '';
    return (first.charAt(0) + last.charAt(0)).toUpperCase() || '?';
}

// --- Component ---

const MyDashboard = (props) => {
    const [user, setUser] = useState(null);
    const [posList, setPosList] = useState([]);
    const [posLoading, setPosLoading] = useState(true);
    const [candidaturesCount, setCandidaturesCount] = useState(0);
    const [sdoRegistrations, setSdoRegistrations] = useState([]);
    const [deletePopupOpen, setDeletePopupOpen] = useState(false);
    const [selectedCollaborator, setSelectedCollaborator] = useState(null);
    const [collaborators, setCollaborators] = useState([]);
    const [accountFeedback, setAccountFeedback] = useState(null);
    const [paymentPopupOpen, setPaymentPopupOpen] = useState(false);
    const { i18n } = useTranslation('common');
    const fr = i18n.language === 'fr';
    const navigate = useNavigate();
    const location = useLocation();

    // Auth guard
    useEffect(() => {
        if (props.fullUserLoading) return;
        if (!props.fullUser?.user) {
            navigate(createUrlWithRedirect('/login', location.pathname + location.search));
            return;
        }
        setUser(props.fullUser);
    }, [props.fullUserLoading, props.fullUser]);

    // Auto-open payment popup if ?renew=true (from HubSpot renewal email)
    useEffect(() => {
        if (!user) return;
        const params = new URLSearchParams(location.search);
        if (params.get('renew') === 'true' && (NeedToPay(user.hsProfile) || IsExpiringSoon(user.hsProfile))) {
            setPaymentPopupOpen(true);
        }
    }, [user, location.search]);

    // Fetch POS
    useEffect(() => {
        if (!user?.user?._id) return;
        api.GetMyPOS({ userid: user.user._id })
            .then(res => setPosList(res.data || []))
            .catch(err => console.error(err))
            .finally(() => setPosLoading(false));
    }, [user]);

    // Fetch candidatures count (MongoDB)
    useEffect(() => {
        if (!user?.user?._id) return;
        api.getUserParticipations({ userid: user.user._id })
            .then(res => setCandidaturesCount(res.data?.length || 0))
            .catch(err => console.error(err));
    }, [user]);

    // Fetch SDO/SDE registrations (Supabase)
    useEffect(() => {
        const email = user?.hsProfile?.email || user?.user?.email;
        if (!email) return;
        getUserRegistrations(email)
            .then(res => setSdoRegistrations(res.data || []))
            .catch(err => console.error(err));
    }, [user]);

    // Fetch collaborators (needed for delete account transfer)
    useEffect(() => {
        if (!user?.company?.hubspotID) return;
        api.GetCollaborators({ hubspotID: user.company.hubspotID })
            .then(res => setCollaborators(res.data || []))
            .catch(err => console.error(err));
    }, [user?.company]);

    // Reset collaborator selection when popup closes
    useEffect(() => {
        setSelectedCollaborator(null);
    }, [deletePopupOpen]);

    function handleResetPassword() {
        const userEmail = user.hsProfile?.email || user.user?.email;
        if (!userEmail) return;
        api.ResetPassword({ email: userEmail })
            .then(() => {
                setAccountFeedback({ type: 'success', message: fr ? 'Un email de réinitialisation a été envoyé à ' + userEmail : 'A reset email has been sent to ' + userEmail });
            })
            .catch(() => {
                setAccountFeedback({ type: 'error', message: fr ? 'Erreur lors de l\'envoi de l\'email' : 'Error sending reset email' });
            });
        setTimeout(() => setAccountFeedback(null), 8000);
    }

    function handleDeleteAccount() {
        if (!window.confirm(fr ? 'Êtes-vous sûr ? Cette action est irréversible.' : 'Are you sure? This action is irreversible.')) return;
        const userIdToDelete = user.user._id;
        api.DeleteAccount({ userIdToDelete, selectedCollaborator })
            .then(() => {
                api.Logout().then(() => {
                    props.refreshUser();
                    navigate('/annuaire');
                });
            })
            .catch(err => {
                console.error(err);
                setAccountFeedback({ type: 'error', message: fr ? 'Erreur lors de la suppression du compte' : 'Error deleting account' });
                setDeletePopupOpen(false);
            });
    }

    if (props.fullUserLoading || !user) {
        return <LoadingContainer><Spinner /></LoadingContainer>;
    }

    const completeness = calculateCompleteness(user);
    const firstName = user.hsProfile?.firstname || user.user?.first_name || '';
    const lastName = user.hsProfile?.lastname || user.user?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim() || user.user?.email;
    const jobTitle = user.hsProfile?.jobtitle || '';
    const companyName = user.hsCompany?.properties?.name || '';
    const city = user.hsProfile?.city || '';
    const country = user.hsProfile?.country || '';
    const locationStr = [city, country].filter(Boolean).join(', ');
    const email = user.hsProfile?.email || user.user?.email || '';
    const projects = user.company?.projects || [];
    const hasCompany = !!user.company?._id;
    const isOwner = user.company?.ownerUserId === user.user?._id;
    const userIsCompanyOwner = hasCompany && isOwner;
    const isMember = !NeedToPay(user.hsProfile);
    const isExpiredMember = !isMember && user.hsProfile?.acheteur?.includes('Oui') && user.hsProfile?.date_adhesion_club_le_french_design;
    const isExpiringSoonMember = IsExpiringSoon(user.hsProfile);
    const isCodifab = user.hsProfile?.adherent?.includes('CODIFAB');
    const isAmeublementFr = user.hsProfile?.adherent?.includes('Ameublement Français');
    const companyLink = isOwner ? '/mycompany' : '/company?id=' + user.company?._id;
    const projectsLink = isOwner ? '/mycompany?snap_to_projects=true' : '/company?id=' + user.company?._id + '&snap_to_projects=true';

    const portraitUrl = user.user?.portrait ? MEDIA_BASE + user.user.portrait : null;
    const companyLogoUrl = user.company?.logo ? MEDIA_BASE + user.company.logo : null;

    return (
        <DashboardContainer>
            {/* Hero */}
            <HeroSection>
                <AvatarCircle>
                    {portraitUrl
                        ? <img src={portraitUrl} alt={fullName} />
                        : <span>{getInitials(user)}</span>
                    }
                </AvatarCircle>
                <HeroInfo>
                    <h1>{fullName}</h1>
                    {jobTitle && <span className="job-title">{jobTitle}</span>}
                    {companyName && <span className="company-name">{companyName}</span>}
                </HeroInfo>
            </HeroSection>

            {/* Completion bar */}
            <CompletionSection>
                <CompletionLabel>
                    <span>{fr ? 'Profil complété' : 'Profile completed'}</span>
                    <strong>{completeness.percentage}%</strong>
                </CompletionLabel>
                <CompletionBarContainer>
                    <CompletionBarFill $percentage={completeness.percentage} />
                </CompletionBarContainer>
            </CompletionSection>

            {/* Renewal Banner for expired members */}
            {isExpiredMember && (
                <RenewalBanner>
                    <div className="renewal-text">
                        <strong>{fr ? 'Votre adhésion a expiré' : 'Your membership has expired'}</strong>
                        {fr ? 'Renouvelez votre adhésion pour continuer à accéder à tous les services de la plateforme.' : 'Renew your membership to keep accessing all platform services.'}
                    </div>
                    <button className="renewal-button" onClick={() => setPaymentPopupOpen(true)}>
                        {fr ? 'Renouveler (35€)' : 'Renew (€35)'}
                    </button>
                </RenewalBanner>
            )}

            {/* Renewal Banner for expiring-soon members */}
            {isExpiringSoonMember && (
                <RenewalBanner style={{ background: '#fff8e1', borderColor: '#f9a825' }}>
                    <div className="renewal-text">
                        <strong>{fr ? 'Votre adhésion expire bientôt' : 'Your membership is expiring soon'}</strong>
                        {fr ? 'Renouvelez dès maintenant pour ne pas perdre l\'accès aux services de la plateforme.' : 'Renew now to keep your access to all platform services.'}
                    </div>
                    <button className="renewal-button" style={{ background: '#f9a825' }} onClick={() => setPaymentPopupOpen(true)}>
                        {fr ? 'Renouveler (35€)' : 'Renew (€35)'}
                    </button>
                </RenewalBanner>
            )}

            {/* Quick Stats */}
            <QuickStatsRow>
                <StatItem>
                    <div className="stat-number">{projects.length}</div>
                    <div className="stat-label">{fr ? 'Projets' : 'Projects'}</div>
                </StatItem>
                {/* <StatItem>
                    <div className="stat-number">{posLoading ? '-' : posList.length}</div>
                    <div className="stat-label">{fr ? 'Points de vente' : 'Points of Sale'}</div>
                </StatItem> */}
                <StatItem>
                    <div className="stat-number">{candidaturesCount + sdoRegistrations.length}</div>
                    <div className="stat-label">{fr ? 'Candidatures' : 'Applications'}</div>
                </StatItem>
                <StatItem>
                    <div className="stat-number">{completeness.percentage}%</div>
                    <div className="stat-label">{fr ? 'Complétion' : 'Completion'}</div>
                </StatItem>
            </QuickStatsRow>

            {/* Cards */}
            <CardsGrid>
                {/* Profile Card */}
                <DashboardCard>
                    <CardHeader>
                        <h3>{fr ? 'Mon profil' : 'My profile'}</h3>
                        <EditLink to="/myprofile">{fr ? 'Modifier' : 'Edit'}</EditLink>
                    </CardHeader>
                    <CardBody>
                        <ProfileInfo>
                            {locationStr && <div className="info-row"><span className="info-icon">&#9679;</span>{locationStr}</div>}
                            <div className="info-row"><span className="info-icon">&#9993;</span>{email}</div>
                            {(user.hsProfile?.phone || user.hsProfile?.mobilephone) &&
                                <div className="info-row"><span className="info-icon">&#9742;</span>{user.hsProfile.mobilephone || user.hsProfile.phone}</div>
                            }
                            <div className="social-row">
                                <span className={`social-badge ${user.hsProfile?.lien_linkedin ? '' : 'inactive'}`}>LinkedIn</span>
                                <span className={`social-badge ${user.hsProfile?.instagram_account ? '' : 'inactive'}`}>Instagram</span>
                                <span className={`social-badge ${user.hsProfile?.twitterhandle ? '' : 'inactive'}`}>X</span>
                            </div>
                            {isMember ? (
                                <MembershipBadge className="active">
                                    {fr ? 'Membre actif' : 'Active member'}{isCodifab && ' (CODIFAB)'}{isAmeublementFr && ' (Ameublement Français)'}
                                </MembershipBadge>
                            ) : (
                                <ActionButton className="reset-password" style={{ marginTop: '10px', fontSize: '12px', padding: '6px 14px' }} onClick={() => setPaymentPopupOpen(true)}>
                                    {fr ? 'Devenir membre' : 'Become a member'}
                                </ActionButton>
                            )}
                        </ProfileInfo>
                    </CardBody>
                </DashboardCard>

                {/* Company Card */}
                <DashboardCard>
                    <CardHeader>
                        <h3>{fr ? 'Ma société' : 'My company'}</h3>
                        {hasCompany
                            ? <EditLink to={companyLink}>{fr ? 'Modifier' : 'Edit'}</EditLink>
                            : <EditLink to="/mycompany">{fr ? 'Créer' : 'Create'}</EditLink>
                        }
                    </CardHeader>
                    <CardBody>
                        {hasCompany ? (
                            <CompanyInfo>
                                {companyLogoUrl
                                    ? <img className="company-logo" src={companyLogoUrl} alt={companyName} />
                                    : <div className="company-logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#0039d6', fontWeight: 600 }}>{companyName.charAt(0)}</div>
                                }
                                <div className="company-details">
                                    <span className="company-title">{companyName}</span>
                                    {user.hsCompany?.properties?.address && <span>{user.hsCompany.properties.address}, {user.hsCompany.properties.city || ''}</span>}
                                    {user.hsCompany?.properties?.website && <a href={user.hsCompany.properties.website.startsWith('http') ? user.hsCompany.properties.website : 'https://' + user.hsCompany.properties.website} target="_blank" rel="noreferrer">{user.hsCompany.properties.website}</a>}
                                </div>
                            </CompanyInfo>
                        ) : (
                            <EmptyState>
                                {fr ? 'Aucune société associée' : 'No company linked'}
                                <br />
                                <Link to="/mycompany">{fr ? 'Créer ma société' : 'Create my company'}</Link>
                            </EmptyState>
                        )}
                    </CardBody>
                </DashboardCard>

                {/* Projects Card */}
                <DashboardCard>
                    <CardHeader>
                        <h3>{fr ? 'Mes projets' : 'My projects'} ({projects.length})</h3>
                        {hasCompany && <EditLink to={projectsLink}>{fr ? 'Voir tout' : 'View all'}</EditLink>}
                    </CardHeader>
                    <CardBody>
                        {projects.length > 0 ? (
                            <ProjectThumbnailGrid>
                                {projects.slice(0, 6).map((project) => {
                                    const imgUrl = GetProjectMainImage(project);
                                    return (
                                        <ProjectThumbnail key={project._id}>
                                            {imgUrl && <img src={imgUrl} alt={project.name || ''} />}
                                        </ProjectThumbnail>
                                    );
                                })}
                            </ProjectThumbnailGrid>
                        ) : (
                            <EmptyState>
                                {fr ? 'Aucun projet' : 'No projects yet'}
                                {hasCompany && <>
                                    <br />
                                    <Link to={projectsLink}>{fr ? 'Ajouter un projet' : 'Add a project'}</Link>
                                </>}
                            </EmptyState>
                        )}
                    </CardBody>
                </DashboardCard>

                {/* Points of Sale Card - hidden for now (WIP)
                <DashboardCard>
                    <CardHeader>
                        <h3>{fr ? 'Points de vente' : 'Points of Sale'} ({posLoading ? '...' : posList.length})</h3>
                        <EditLink to="/mypointofsale">{fr ? 'Gérer' : 'Manage'}</EditLink>
                    </CardHeader>
                    <CardBody>
                        {posLoading ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}><Spinner /></div>
                        ) : posList.length > 0 ? (
                            <POSList>
                                {posList.slice(0, 3).map((pos) => (
                                    <POSItem key={pos.id}>
                                        {pos.logo
                                            ? <img className="pos-logo" src={MEDIA_BASE + pos.logo} alt={pos.titre_affiche} />
                                            : <div className="pos-placeholder">&#9679;</div>
                                        }
                                        <div className="pos-info">
                                            <div className="pos-title">{pos.titre_affiche}</div>
                                            {pos.adresse && <div className="pos-address">{pos.adresse}</div>}
                                        </div>
                                    </POSItem>
                                ))}
                                {posList.length > 3 && (
                                    <div style={{ fontSize: '13px', color: '#777', textAlign: 'center', paddingTop: '4px' }}>
                                        +{posList.length - 3} {fr ? 'autre(s)' : 'more'}
                                    </div>
                                )}
                            </POSList>
                        ) : (
                            <EmptyState>
                                {fr ? 'Aucun point de vente' : 'No points of sale yet'}
                                <br />
                                <Link to="/mypointofsale">{fr ? 'Ajouter un point de vente' : 'Add a point of sale'}</Link>
                            </EmptyState>
                        )}
                    </CardBody>
                </DashboardCard> */}

                {/* Candidatures Card */}
                <DashboardCard>
                    <CardHeader>
                        <h3>{fr ? 'Mes candidatures' : 'My applications'}</h3>
                        {candidaturesCount > 0 && <EditLink to="/mycandidatures">{fr ? 'Voir' : 'View'}</EditLink>}
                    </CardHeader>
                    <CardBody>
                        {candidaturesCount > 0 && (
                            <ProfileInfo>
                                <div className="info-row" style={{ fontSize: '15px' }}>
                                    <strong style={{ color: '#0039d6', fontSize: '20px', marginRight: '8px' }}>{candidaturesCount}</strong>
                                    {fr ? 'candidature(s) FD100' : 'FD100 application(s)'}
                                </div>
                            </ProfileInfo>
                        )}
                        {sdoRegistrations.length > 0 && (
                            <div style={{ marginTop: candidaturesCount > 0 ? '12px' : 0 }}>
                                {sdoRegistrations.map(reg => (
                                    <div key={reg.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#f0f7ff', borderRadius: '8px', marginBottom: '8px' }}>
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: '14px', color: '#222' }}>
                                                {reg.event?.title_fr || 'Speed Dating Design Objet 2026'}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                                {reg.population_type === 'pop_a' ? (fr ? 'Fabricant / Éditeur' : 'Manufacturer / Publisher') : (fr ? 'Designer / Créateur' : 'Designer / Creator')}
                                                {' — '}
                                                <span style={{ color: reg.status === 'submitted' ? '#2e7d32' : '#e65100', fontWeight: 500 }}>
                                                    {reg.status === 'submitted' ? (fr ? 'Soumise' : 'Submitted') : reg.status === 'validated' ? (fr ? 'Validée' : 'Validated') : (fr ? 'Brouillon' : 'Draft')}
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                            <Link to={`/evenements/sdo/register?eid=${reg.event_id}&pop=${reg.population_type === 'pop_a' ? 'a' : 'b'}`} style={{ fontSize: '13px', color: '#0039d6', textDecoration: 'none', fontWeight: 500 }}>
                                                {reg.status === 'draft' ? (fr ? 'Compléter' : 'Complete') : (fr ? 'Modifier' : 'Edit')}
                                            </Link>
                                            {reg.population_type === 'pop_b' && reg.status === 'submitted' && (
                                                <Link to={`/evenements/sdo/candidatures?eid=${reg.event_id}`} style={{ fontSize: '13px', color: '#fff', background: '#0039d6', textDecoration: 'none', fontWeight: 500, padding: '4px 10px', borderRadius: '6px' }}>
                                                    {fr ? 'Candidatures' : 'Applications'}
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {candidaturesCount === 0 && sdoRegistrations.length === 0 && (
                            <EmptyState>
                                {fr ? 'Aucune candidature en cours' : 'No active applications'}
                            </EmptyState>
                        )}
                    </CardBody>
                </DashboardCard>
            </CardsGrid>

            {/* Account Actions */}
            <AccountSection>
                <h3>{fr ? 'Paramètres du compte' : 'Account settings'}</h3>
                <AccountActions>
                    <ActionButton className="reset-password" onClick={handleResetPassword}>
                        {fr ? 'Réinitialiser le mot de passe' : 'Reset password'}
                    </ActionButton>
                    <ActionButton className="delete-account" onClick={() => setDeletePopupOpen(true)}>
                        {fr ? 'Supprimer mon compte' : 'Delete my account'}
                    </ActionButton>
                </AccountActions>
                {accountFeedback && (
                    <FeedbackMessage className={accountFeedback.type}>
                        {accountFeedback.message}
                    </FeedbackMessage>
                )}
            </AccountSection>

            {/* Delete Account Popup */}
            {deletePopupOpen && (
                <ConfirmPopup onClick={() => setDeletePopupOpen(false)}>
                    <ConfirmPopupInner onClick={e => e.stopPropagation()}>
                        <h3>{fr ? 'Suppression du compte' : 'Delete account'}</h3>
                        {!userIsCompanyOwner ? (
                            <>
                                <p>{fr ? 'Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.' : 'Are you sure you want to delete your account? This action is irreversible.'}</p>
                                <div className="popup-actions">
                                    <button className="popup-cancel" onClick={() => setDeletePopupOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</button>
                                    <button className="popup-confirm" onClick={handleDeleteAccount}>{fr ? 'Supprimer' : 'Delete'}</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <p>{fr ? `Vous êtes le responsable de la société ${companyName}. Pour garder votre société sur la plateforme, transférez la responsabilité à un collaborateur.` : `You are the owner of ${companyName}. To keep your company on the platform, transfer ownership to a collaborator.`}</p>
                                <select onChange={e => setSelectedCollaborator(collaborators.find(c => c.user._id === e.target.value))} value={selectedCollaborator?.user?._id || ''}>
                                    <option value="">{fr ? 'Choisir un collaborateur' : 'Choose a collaborator'}</option>
                                    {collaborators.filter(c => c.user._id !== user.user._id).map(c => (
                                        <option key={c.user._id} value={c.user._id}>{c.hsProfile?.properties?.firstname} {c.hsProfile?.properties?.lastname}</option>
                                    ))}
                                </select>
                                {!selectedCollaborator && (
                                    <p className="warning">{fr ? 'Sans transfert, votre société et tous les collaborateurs seront supprimés.' : 'Without transfer, your company and all collaborators will be deleted.'}</p>
                                )}
                                <div className="popup-actions">
                                    <button className="popup-cancel" onClick={() => setDeletePopupOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</button>
                                    <button className="popup-confirm" onClick={handleDeleteAccount}>
                                        {selectedCollaborator
                                            ? (fr ? 'Transférer et supprimer' : 'Transfer and delete')
                                            : (fr ? 'Supprimer tout' : 'Delete all')
                                        }
                                    </button>
                                </div>
                            </>
                        )}
                    </ConfirmPopupInner>
                </ConfirmPopup>
            )}

            {paymentPopupOpen && <PaymentPopupV2 close={() => setPaymentPopupOpen(false)} email={email} />}
        </DashboardContainer>
    );
};

export default MyDashboard;
